import type { StyleLibrary } from "schemas/generated/project";

import * as React from "react";

import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useFetchLogoForWebContainer } from "@editor/hooks/designLibrary/useFetchLogoForWebContainer";
import { useInsertGoogleFontsUrl } from "@editor/hooks/designLibrary/useInsertGoogleFontsUrl";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { projectRouter, projectUtils } from "@editor/trpc/project";
import { shopMetadataRouter } from "@editor/trpc/shopMetadata";

import Button from "@replo/design-system/components/button/Button";
import IconButton from "@replo/design-system/components/button/IconButton";
import twMerge from "@replo/design-system/utils/twMerge";
import { Pencil, ShoppingCart, Upload } from "lucide-react";
import { ReploError } from "schemas/errors";

class LogoUploadError extends ReploError {}

interface DesignLibrarySummaryProps {
  styleLibrary: StyleLibrary | null;
  onColorsClick?: () => void;
  onTypographyClick?: () => void;
  onCustomFontsClick?: () => void;
  onStyleLibraryChange?: (styleLibrary: StyleLibrary) => void;
}

export const DesignLibrarySummary: React.FC<DesignLibrarySummaryProps> = ({
  styleLibrary,
  onColorsClick,
  onTypographyClick,
  onCustomFontsClick,
  onStyleLibraryChange,
}) => {
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProjectContext();
  const { updateLogo } = useFetchLogoForWebContainer();

  const fonts = React.useMemo(() => {
    if (!styleLibrary?.typography) {
      return [];
    }

    return Object.values(styleLibrary.typography).map((font) => ({
      name: font.name,
      weight: font.weight,
    }));
  }, [styleLibrary?.typography]);

  useInsertGoogleFontsUrl(fonts);

  const handleCornerRoundingChange = async (cornerRoundingValue: number) => {
    if (!styleLibrary || !onStyleLibraryChange) {
      return;
    }

    onStyleLibraryChange({
      ...styleLibrary,
      cornerRounding: cornerRoundingValue,
    });
  };

  const cornerRoundingOptions = [
    { label: "None", value: 0 },
    { label: "Small", value: 4 },
    { label: "Medium", value: 8 },
    { label: "Large", value: 12 },
  ];

  // Extract values from styleLibrary
  const colors = styleLibrary?.colors;
  const themeColors = {
    light: colors?.neutrals.light ?? "#FFFFFF",
    subtle: colors?.neutrals.subtle ?? "#FFFFFF",
    muted: colors?.neutrals.muted ?? "#FFFFFF",
    dark: colors?.neutrals.dark ?? "#FFFFFF",
    primary: colors?.brand.primary.color ?? "#FFFFFF",
    secondary: colors?.brand.secondary.color ?? "#FFFFFF",
    tertiary: colors?.brand.tertiary.color ?? null,
    accent: colors?.brand.accent.color ?? null,
  };

  const h1Typography = styleLibrary?.typography?.h1;
  const bodyTypography = styleLibrary?.typography?.body;
  const cornerRounding = styleLibrary?.cornerRounding;

  const [isLogoUploadLoading, setIsLogoUploadLoading] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { mutateAsync: updateProject } = projectRouter.update.useMutation({
    onSuccess: () => {
      void projectUtils.getV2.invalidate();
    },
  });

  const { mutateAsync: generateSignedUrlForLogo } =
    shopMetadataRouter.generateSignedUrlForLogo.useMutation();

  const handleUploadLogo = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (
      !file ||
      (!file.type.startsWith("image/") && file.type !== "image/svg+xml")
    ) {
      return;
    }

    if (!projectId || !project) {
      return;
    }

    try {
      setIsLogoUploadLoading(true);

      const { signedUrl, publicUrl } = await generateSignedUrlForLogo({
        projectId,
        contentType: file.type,
        contentLength: file.size,
      });

      const uploadResponse = await fetch(signedUrl, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": file.type,
          "Content-Length": file.size.toString(),
        },
      });

      if (!uploadResponse.ok) {
        throw new LogoUploadError({
          message: `Failed to upload logo ${file.name} to Cloudflare`,
          additionalData: { status: uploadResponse.status },
        });
      }

      await updateProject({
        projectId,
        project: {
          logoUrl: publicUrl,
        },
      });

      await updateLogo();
    } finally {
      setIsLogoUploadLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-start flex-[1_0_0] self-stretch min-h-0 rounded-xl border border-border bg-white shadow-button-selectable overflow-y-auto no-scrollbar">
      <div
        className="flex flex-1 p-4 flex-col items-start gap-6 self-stretch border-b-0.5 border-border cursor-pointer hover:bg-background transition-colors"
        onClick={onColorsClick}
      >
        <div className="flex justify-between items-start self-stretch">
          <div className="header-h4">Colors</div>
          <Button
            variant="secondary"
            size="sm"
            startEnhancer={<Pencil size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              onColorsClick?.();
            }}
          >
            Edit
          </Button>
        </div>
        {themeColors && (
          <div className="flex items-center self-stretch">
            {Object.entries(themeColors)
              .filter(([, color]) => Boolean(color))
              .map(([key, color], index) => (
                <div
                  key={key}
                  className={twMerge(
                    "h-16 rounded-lg border-0.5 border-black/10",
                    index > 0 ? "-ml-4" : "",
                  )}
                  style={{
                    backgroundColor: color ?? "",
                    flex: "1 0 0",
                    alignSelf: "stretch",
                  }}
                ></div>
              ))}
          </div>
        )}
      </div>
      <div
        className="flex flex-2 p-4 flex-col items-start gap-6 self-stretch border-b-0.5 border-border cursor-pointer hover:bg-background transition-colors"
        onClick={onTypographyClick}
      >
        <div className="flex justify-between items-start self-stretch">
          <div className="header-h4">Typography</div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onCustomFontsClick?.();
              }}
            >
              Custom Fonts
            </Button>
            <Button
              variant="secondary"
              size="sm"
              startEnhancer={<Pencil size={12} />}
              onClick={(e) => {
                e.stopPropagation();
                onTypographyClick?.();
              }}
            >
              Edit
            </Button>
          </div>
        </div>
        <div className="flex flex-col p-3 justify-center items-center self-stretch rounded-lg bg-sheet border-0.5 border-black/10 gap-1">
          <div
            className="flex h-8 flex-col justify-center items-center self-stretch text-center"
            style={{
              fontFamily: h1Typography?.name
                ? `'${h1Typography.name}', sans-serif`
                : undefined,
              fontSize: "42px",
              fontWeight: h1Typography?.weight ?? 600,
            }}
          >
            Heading
          </div>
          <div
            style={{
              fontFamily: bodyTypography?.name
                ? `'${bodyTypography.name}', sans-serif`
                : undefined,
              fontSize: "16px",
              fontWeight: bodyTypography?.weight ?? 400,
            }}
          >
            This is your paragraph.
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 self-stretch">
          <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
            <div className="text-sm font-medium text-muted">Headings</div>
            <div
              className="text-3xl font-semibold truncate min-w-0 w-full"
              style={{
                fontFamily: h1Typography?.name
                  ? `'${h1Typography.name}', sans-serif`
                  : undefined,
              }}
              title={h1Typography?.name ?? "Default"}
            >
              {h1Typography?.name ?? "Default"}
            </div>
          </div>
          <div className="flex py-3 pr-1 pl-3 flex-col items-start gap-2 border-0.5 border-border rounded-md overflow-hidden min-w-0">
            <div className="text-sm font-medium text-muted">Body</div>
            <div
              className="text-3xl font-semibold truncate min-w-0 w-full"
              style={{
                fontFamily: bodyTypography?.name
                  ? `'${bodyTypography.name}', sans-serif`
                  : undefined,
              }}
              title={bodyTypography?.name ?? "Default"}
            >
              {bodyTypography?.name ?? "Default"}
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-1 p-4 flex-col items-start gap-6 self-stretch border-b-0.5 border-border">
        <div className="flex justify-between items-start self-stretch">
          <div className="header-h4">Corner Rounding</div>
        </div>
        <div className="flex items-start gap-3 self-stretch">
          {cornerRoundingOptions.map((option) => {
            const isSelected = cornerRounding === option.value;
            return (
              <div
                key={option.value}
                className={twMerge(
                  "flex flex-col gap-2 p-2 justify-between items-center flex-1 self-stretch border-0.5 rounded-lg cursor-pointer",
                  isSelected ? "border-info bg-info-soft" : "border-border",
                )}
                onClick={() => void handleCornerRoundingChange(option.value)}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 21 21"
                  fill="none"
                >
                  {option.value === 0 && (
                    <path
                      d="M0.75 20.8V0.800049H20.75"
                      stroke={isSelected ? "var(--color-info)" : "#475569"}
                    />
                  )}
                  {option.value === 4 && (
                    <path
                      d="M1.25 20.8V3.80005C1.25 2.14319 2.59315 0.800049 4.25 0.800049H21.25"
                      stroke={isSelected ? "var(--color-info)" : "#475569"}
                    />
                  )}
                  {option.value === 8 && (
                    <path
                      d="M0.75 20.8V6.80005C0.75 3.48634 3.43629 0.800049 6.75 0.800049H20.75"
                      stroke={isSelected ? "var(--color-info)" : "#475569"}
                    />
                  )}
                  {option.value === 12 && (
                    <path
                      d="M1.25 20.8V8.80005C1.25 4.38177 4.83172 0.800049 9.25 0.800049H21.25"
                      stroke={isSelected ? "var(--color-info)" : "#475569"}
                    />
                  )}
                </svg>
                <div
                  className={twMerge(
                    "text-sm font-medium",
                    isSelected ? "text-info" : "text-muted",
                  )}
                >
                  {option.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex flex-1 p-4 flex-col items-start gap-6 self-stretch hover:bg-background transition-colors">
        <div className="flex justify-between items-start self-stretch">
          <div className="header-h4">Site Logo</div>
          <Button
            variant="secondary"
            size="sm"
            startEnhancer={<Upload size={12} />}
            onClick={handleUploadLogo}
            isLoading={isLogoUploadLoading}
            disabled={isLogoUploadLoading}
          >
            {project?.logoUrl ? "Replace" : "Upload"}
          </Button>
        </div>
        <div className="text-sm text-muted -mt-5">
          Upload a PNG, JPG, or SVG logo for your site header.
        </div>
        <div className="flex items-center justify-center self-stretch p-4 pr-0 rounded-lg bg-sheet border-0.5 border-black/10 min-h-24">
          <div className="flex items-center justify-between p-4 bg-white w-full h-full rounded-l-lg">
            <div className="flex items-center">
              <img
                src={project?.logoUrl ?? "/your-logo.svg"}
                alt="Site Logo"
                className="max-h-12 max-w-24 object-contain"
              />
            </div>
            <IconButton
              variant="secondary"
              size="base"
              icon={<ShoppingCart size={16} />}
              UNSAFE_className="pointer-events-none cursor-default"
            />
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
            onChange={(e) => void handleFileChange(e)}
            className="hidden"
          />
        </div>
      </div>
    </div>
  );
};
